import React, { useState, useRef } from "react";
import {
  Camera, Microphone, MapPin, Gauge, ArrowsClockwise, Ruler, Sun, Compass, Thermometer, DeviceMobile,
  X, ShieldCheck, CheckCircle, WarningCircle, CaretLeft,
} from "@phosphor-icons/react";
import { SENSOR_DEFS, runMeasurement } from "../lib/sensors";

const ICONS = { Camera, Microphone, MapPin, Gauge, ArrowsClockwise, Ruler, Sun, Compass, Thermometer, DeviceMobile };
const DURATIONS = [10, 30, 60];

export default function SensorSheet({ open, onClose, onApply }) {
  const [step, setStep] = useState("select");
  const [selected, setSelected] = useState([]);
  const [duration, setDuration] = useState(30);
  const [progress, setProgress] = useState(0);
  const [live, setLive] = useState({});
  const [results, setResults] = useState(null);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);
  const measurementRunRef = useRef(0);

  if (!open) return null;

  const reset = () => {
    setStep("select");
    setSelected([]);
    setProgress(0);
    setLive({});
    setResults(null);
  };

  const close = () => {
    cancelledRef.current = true;
    measurementRunRef.current += 1;
    if (timerRef.current) clearInterval(timerRef.current);
    reset();
    onClose();
  };

  const cancelMeasurement = () => {
    cancelledRef.current = true;
    measurementRunRef.current += 1;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setProgress(0);
    setLive({});
    setResults(null);
    setStep("select");
  };

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const startMeasurement = async () => {
    setStep("measuring");
    setProgress(0);
    setLive({});
    cancelledRef.current = false;
    measurementRunRef.current += 1;
    const runId = measurementRunRef.current;
    const startMs = Date.now();
    timerRef.current = setInterval(() => {
      if (cancelledRef.current || measurementRunRef.current !== runId) return;
      setProgress(Math.min(1, (Date.now() - startMs) / (duration * 1000)));
    }, 100);
    const payload = await runMeasurement(selected, duration, (id, text) => {
      if (cancelledRef.current || measurementRunRef.current !== runId) return;
      setLive((l) => ({ ...l, [id]: text }));
    });
    clearInterval(timerRef.current);
    timerRef.current = null;
    if (cancelledRef.current || measurementRunRef.current !== runId) return;
    setProgress(1);
    setResults(payload);
    setStep("preview");
  };

  const remaining = Math.max(0, Math.ceil(duration * (1 - progress)));
  const R = 54;
  const circ = 2 * Math.PI * R;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60 backdrop-blur-xl">
      <div className="flex-1 overflow-y-auto max-w-lg mx-auto w-full bg-bg border-x border-white/10 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-bg z-10">
          <div className="flex items-center gap-2">
            {step !== "select" && step !== "measuring" && (
              <button data-testid="sensor-back-btn" onClick={() => setStep("select")} className="p-1 text-muted hover:text-white">
                <CaretLeft size={18} weight="bold" />
              </button>
            )}
            <h2 className="font-heading text-xl font-bold tracking-tight">
              {step === "select" && "UMGEBUNGSDATEN HINZUFÜGEN"}
              {step === "consent" && "EINWILLIGUNG"}
              {step === "measuring" && "MESSUNG LÄUFT"}
              {step === "preview" && "MESSERGEBNIS"}
            </h2>
          </div>
          {step !== "measuring" && (
            <button data-testid="sensor-close-btn" onClick={close} className="p-2 rounded-full bg-elevated border border-white/10">
              <X size={16} weight="bold" />
            </button>
          )}
        </div>

        {step === "select" && (
          <div className="p-4 pb-28">
            <p className="text-muted text-sm mb-4">
              Wähle die Sensoren aus, die gemessen werden sollen. Gemessen wird nur nach deiner ausdrücklichen Zustimmung – nie im Hintergrund.
            </p>
            <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted mb-2">Messdauer</div>
              <div className="flex gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    data-testid={`duration-${d}`}
                    onClick={() => setDuration(d)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                      duration === d ? "bg-volt border-volt text-white" : "bg-surface border-white/10 text-muted"
                    }`}
                  >
                    {d} s
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2.5">
              {SENSOR_DEFS.map((def) => {
                const Icon = ICONS[def.icon];
                const avail = def.available();
                const active = selected.includes(def.id);
                return (
                  <button
                    key={def.id}
                    data-testid={`sensor-card-${def.id}`}
                    onClick={() => avail && toggle(def.id)}
                    disabled={!avail}
                    className={`w-full text-left rounded-xl border p-4 transition-all ${
                      !avail
                        ? "bg-surface/50 border-white/5 opacity-55"
                        : active
                        ? "bg-volt/10 border-volt"
                        : "bg-surface border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon size={24} weight="duotone" className={active ? "text-volt" : "text-muted"} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm">{def.name}</span>
                          {avail ? (
                            <span
                              className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
                                active ? "bg-volt border-volt" : "border-white/25"
                              }`}
                            >
                              {active && <CheckCircle size={14} weight="fill" className="text-white" />}
                            </span>
                          ) : (
                            <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-muted border border-white/10 rounded px-1.5 py-0.5">
                              Nicht verfügbar
                            </span>
                          )}
                        </div>
                        <p className="text-muted text-xs mt-1">{def.desc}</p>
                        <p className="text-xs mt-1 text-volthover/90">Typisch: {def.use}</p>
                        {!avail && def.note && <p className="text-[11px] text-muted mt-1 italic">{def.note}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="fixed bottom-0 left-0 right-0 z-20">
              <div className="max-w-lg mx-auto p-4 bg-gradient-to-t from-bg via-bg to-transparent">
                <button
                  data-testid="start-measurement-btn"
                  onClick={() => setStep("consent")}
                  disabled={selected.length === 0}
                  className="w-full bg-volt hover:bg-volthover disabled:opacity-40 disabled:pointer-events-none active:scale-[0.98] transition-all rounded-xl py-4 font-bold"
                >
                  Messung vorbereiten {selected.length > 0 && `(${selected.length} Sensor${selected.length > 1 ? "en" : ""})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "consent" && (
          <div data-testid="consent-dialog" className="p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 text-volt">
              <ShieldCheck size={28} weight="duotone" />
              <span className="font-heading text-lg font-bold">DEINE EINWILLIGUNG</span>
            </div>
            <p className="text-sm text-muted">
              Die App möchte für <strong className="text-white">{duration} Sekunden</strong> folgende Sensoren auslesen:
            </p>
            <div className="bg-surface border border-white/10 rounded-xl p-4 space-y-2">
              {selected.map((id) => {
                const def = SENSOR_DEFS.find((d) => d.id === id);
                const Icon = ICONS[def.icon];
                return (
                  <div key={id} className="flex items-center gap-2 text-sm">
                    <Icon size={18} weight="duotone" className="text-volt" />
                    {def.name}
                  </div>
                );
              })}
            </div>
            <ul className="text-xs text-muted space-y-1.5 list-disc pl-4">
              <li>Die Daten werden <strong className="text-white">nur lokal auf deinem Gerät</strong> erfasst und zusammengefasst.</li>
              <li>Es findet <strong className="text-white">keine Messung im Hintergrund</strong> statt – nur jetzt, auf deinen Befehl.</li>
              <li>An die KI wird <strong className="text-white">nur die Zusammenfassung</strong> gesendet – und erst, wenn du auf „Senden“ tippst.</li>
              <li>Dein Browser fragt ggf. zusätzlich nach Berechtigungen (Kamera, Mikrofon, Standort).</li>
            </ul>
            <div className="flex gap-2 mt-2">
              <button
                data-testid="consent-cancel-btn"
                onClick={() => setStep("select")}
                className="flex-1 bg-elevated border border-white/10 rounded-xl py-3.5 font-semibold text-sm"
              >
                Abbrechen
              </button>
              <button
                data-testid="consent-accept-btn"
                onClick={startMeasurement}
                className="flex-1 bg-volt hover:bg-volthover active:scale-[0.98] transition-all rounded-xl py-3.5 font-bold text-sm"
              >
                Einverstanden & starten
              </button>
            </div>
          </div>
        )}

        {step === "measuring" && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-8">
            <div className="relative measuring-ring">
              <svg width="140" height="140" viewBox="0 0 140 140" data-testid="measurement-progress">
                <circle cx="70" cy="70" r={R} stroke="#27272A" strokeWidth="8" fill="none" />
                <circle
                  cx="70" cy="70" r={R} stroke="#007AFF" strokeWidth="8" fill="none" strokeLinecap="round"
                  strokeDasharray={circ} strokeDashoffset={circ * (1 - progress)}
                  transform="rotate(-90 70 70)" style={{ transition: "stroke-dashoffset 0.15s linear" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-heading text-4xl font-black">{remaining}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted font-bold">Sekunden</span>
              </div>
            </div>
            <div className="w-full space-y-2">
              {selected.map((id) => {
                const def = SENSOR_DEFS.find((d) => d.id === id);
                const Icon = ICONS[def.icon];
                return (
                  <div key={id} className="flex items-center justify-between bg-surface border border-white/10 rounded-xl px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Icon size={18} weight="duotone" className="text-volt" />
                      <span className="font-medium">{def.name.split(" /")[0]}</span>
                    </div>
                    <span data-testid={`live-value-${id}`} className="text-volthover text-xs font-mono">
                      {live[id] || "…"}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted text-center">Messung läuft – Daten werden lokal erfasst.</p>
            <button
              data-testid="cancel-measurement-btn"
              onClick={cancelMeasurement}
              className="w-full rounded-xl border border-white/10 bg-elevated px-4 py-3 text-sm font-bold text-white hover:border-volt/60 active:scale-[0.98] transition-all"
            >
              Messung abbrechen
            </button>
          </div>
        )}

        {step === "preview" && results && (
          <div className="p-4 pb-28">
            <p className="text-muted text-sm mb-4">
              Zusammenfassung der Messung ({results.window.duration} s). Gesendet wird nur diese Zusammenfassung – keine Rohdaten.
            </p>
            <div className="space-y-3">
              {results.sensors.map((s) => (
                <div key={s.id} data-testid={`preview-${s.id}`} className="bg-surface border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{s.name}</span>
                    {s.error ? (
                      <WarningCircle size={18} weight="fill" className="text-red-400" />
                    ) : (
                      <CheckCircle size={18} weight="fill" className="text-green-400" />
                    )}
                  </div>
                  {s.error ? (
                    <p className="text-xs text-red-300">{s.error}</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {(s.display || []).map((d) => (
                          <div key={`${s.id}-${d.label}`} className="bg-elevated rounded-lg px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wider text-muted font-bold">{d.label}</div>
                            <div className="text-sm font-semibold">{d.value}</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-volthover">{s.interpretation}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="fixed bottom-0 left-0 right-0 z-20">
              <div className="max-w-lg mx-auto p-4 bg-gradient-to-t from-bg via-bg to-transparent flex gap-2">
                <button
                  data-testid="discard-measurement-btn"
                  onClick={reset}
                  className="flex-1 bg-elevated border border-white/10 rounded-xl py-3.5 font-semibold text-sm"
                >
                  Verwerfen
                </button>
                <button
                  data-testid="apply-measurement-btn"
                  onClick={() => {
                    const payload = results;
                    reset();
                    onApply(payload);
                  }}
                  disabled={results.sensors.every((s) => s.error)}
                  className="flex-1 bg-volt hover:bg-volthover disabled:opacity-40 active:scale-[0.98] transition-all rounded-xl py-3.5 font-bold text-sm"
                >
                  In den Chat übernehmen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
